import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { useCriarLancamentoManual } from "@/features/contabil/hooks/useContabilizacao";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";

export default function LancamentoForm() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const { plano: contas = [] } = usePlanoContas();
  // Só contas analíticas (folha) recebem lançamento; sintéticas somam os filhos.
  const contasAnaliticas = contas.filter((c) => c.tipoContaId === 1);
  const criar = useCriarLancamentoManual();

  const [contaDebitoId, setContaDebitoId]   = useState<number>(0);
  const [contaCreditoId, setContaCreditoId] = useState<number>(0);
  const [valor, setValor]                   = useState<string>("");
  const [dtMovimento, setDtMovimento]       = useState(new Date().toISOString().slice(0, 10));
  const [historico, setHistorico]           = useState("");

  const podeSalvar = contaDebitoId > 0 && contaCreditoId > 0
                  && contaDebitoId !== contaCreditoId
                  && Number(valor) > 0;

  const handleSalvar = async () => {
    await criar.mutateAsync({
      empresaId,
      contaContabilDebitoId: contaDebitoId,
      contaContabilCreditoId: contaCreditoId,
      valor: Number(valor),
      dtMovimento,
      historico: historico || undefined,
    });
    setValor("");
    setHistorico("");
  };

  return (
    <div className="container mx-auto p-6 max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Lançamento Manual</h1>
        <p className="text-muted-foreground">
          Partidas dobradas: o débito numa conta deve ser igual ao crédito noutra.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Dados do lançamento</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Conta a debitar</Label>
              <Select value={String(contaDebitoId)} onValueChange={(v) => setContaDebitoId(Number(v))}>
                <SelectTrigger><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>
                  {contasAnaliticas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.codigoPlanoConta} — {c.descricaoConta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conta a creditar</Label>
              <Select value={String(contaCreditoId)} onValueChange={(v) => setContaCreditoId(Number(v))}>
                <SelectTrigger><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>
                  {contasAnaliticas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.codigoPlanoConta} — {c.descricaoConta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} />
            </div>
            <div>
              <Label>Data do movimento</Label>
              <Input type="date" value={dtMovimento} onChange={(e) => setDtMovimento(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Histórico (opcional)</Label>
            <Textarea value={historico} onChange={(e) => setHistorico(e.target.value)} rows={3} />
          </div>

          {contaDebitoId === contaCreditoId && contaDebitoId > 0 && (
            <Alert variant="destructive">
              <AlertDescription>Conta débito e crédito devem ser diferentes.</AlertDescription>
            </Alert>
          )}

          <Button onClick={handleSalvar} disabled={!podeSalvar || criar.isPending}>
            {criar.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Registrar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
